import { KeyboardEvent, MouseEvent, ReactNode, useMemo, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Column } from 'primereact/column';
import { DataTable, DataTableSortEvent } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import InfoTip from '../../components/novarium/InfoTip';
import { modeMessageId, StatisticsMode } from './statisticsMode';
import { ALERT_MIN_ATTENTIONS, WaitLevel } from './alertThresholds';
import { LEVEL_LABEL_ID, LEVEL_TAG_CLASS } from './waitLevelText';
import {
    ALL, formatDecimal, formatInt, formatMinutes, formatPercent, formatSignedPercent, ServiceRow,
} from './statisticsUtils';

interface ServicesTableProps {
    rows: ServiceRow[];
    /** Servicio aplicado como filtro; 0 si no hay. */
    selectedId: number;
    /** Elegir un servicio lo aplica como filtro; elegir el aplicado lo quita (`ALL`). */
    onToggle: (serviceId: number) => void;
    /**
     * Nivel de cada servicio o agenda, el mismo que «Dónde hay más espera» (lo calcula el backend; el tablero ya resolvió
     * cuáles están «En regla»). Los que no están en el mapa (pocas atenciones, o el ranking todavía no está) no llevan etiqueta.
     */
    levels: ReadonlyMap<number, WaitLevel>;
    /** En «Mi desempeño» todas son agendas del profesional. */
    mode?: StatisticsMode;
}

const EMPTY = '—';

interface HintTagProps {
    /** Id único en la pantalla: enlaza la etiqueta con su explicación para el lector de pantalla. */
    id: string;
    className: string;
    label: string;
    /** Qué significa la etiqueta, en una frase. */
    hint: string;
}

/**
 * Etiqueta con su explicación. Se alcanza con Tab (muestra el tooltip al enfocarla, igual que con el mouse), la frase
 * queda en el DOM para el lector de pantalla y, en pantalla táctil, tocarla muestra el detalle sin filtrar la fila.
 */
function HintTag({ id, className, label, hint }: HintTagProps) {
    const stop = (event: MouseEvent | KeyboardEvent) => event.stopPropagation();
    return (
        <span className="nv-tip ss-reason" tabIndex={0} data-pr-tooltip={hint} aria-describedby={id}
            onClick={stop} onKeyDown={stop}>
            <Tag className={className} value={label} />
            <span id={id} className="nv-sr-only">{hint}</span>
        </span>
    );
}

interface ReasonsCellProps {
    intl: IntlShape;
    row: ServiceRow;
}

/** «Por qué»: las etiquetas de la agenda (turno corto, termina tarde, sobreturnos) en una sola celda. */
function ReasonsCell({ intl, row }: ReasonsCellProps) {
    const tags: { key: string; label: string; hint: string }[] = [];
    if (row.shortSlot !== null) {
        tags.push({
            key: 'short',
            label: intl.formatMessage({ id: 'ServicesStatisticsShortSlotTag' }),
            hint: intl.formatMessage({ id: 'ServicesStatisticsShortSlotHint' }, { suggested: formatInt(row.shortSlot.suggestedMinutes) }),
        });
    }
    if (row.overrun !== null) {
        tags.push({
            key: 'overrun',
            label: intl.formatMessage({ id: 'ServicesStatisticsOverrunTag' }),
            hint: intl.formatMessage({ id: 'ServicesStatisticsOverrunHint' }, {
                days: formatInt(row.overrun.days),
                scheduleDays: formatInt(row.overrun.scheduleDays),
                avg: formatDecimal(row.overrun.avgMinutes),
                max: formatMinutes(row.overrun.maxMinutes),
            }),
        });
    }
    if (row.overbookedRatio !== null) {
        tags.push({
            key: 'overbooked',
            label: intl.formatMessage({ id: 'ServicesStatisticsOverbookedTag' }),
            hint: intl.formatMessage({ id: 'ServicesStatisticsOverbookedRowHint' }, { ratio: formatPercent(row.overbookedRatio) }),
        });
    }
    if (tags.length === 0) return <>{EMPTY}</>;
    return (
        <span className="ss-reasons" role="group"
            aria-label={intl.formatMessage({ id: 'ServicesStatisticsColWhyLabel' }, { reasons: tags.map(tag => tag.label).join(', ') })}>
            {tags.map(tag => (
                <HintTag key={tag.key} id={`ss-reason-${row.serviceId}-${tag.key}`} className="nv-tag-warn"
                    label={tag.label} hint={tag.hint} />
            ))}
        </span>
    );
}

/**
 * Servicios y agendas del período, con orden y búsqueda en el front (el dataset ya está completo y es chico, sin
 * paginación). Salen primero los de mayor espera media, que además lleva el nivel del ranking, con texto. La duración real de los
 * consultorios va con el turno programado. Las etiquetas de la agenda («Turno corto», «Termina tarde», «Sobreturnos»)
 * están juntas en la columna «Por qué», cada una con su explicación. Cabe en una notebook sin scroll horizontal: el
 * nombre es la columna que se achica y se corta con "…". Tocar una fila, o Enter o espacio con la fila enfocada, filtra
 * la pantalla por ese servicio.
 */
export default function ServicesTable({ rows, selectedId, onToggle, levels, mode = 'management' }: ServicesTableProps) {
    const intl = useIntl();
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState<{ field: string; order: 1 | -1 }>({ field: 'avgWait', order: -1 });

    const visible = useMemo(() => {
        const text = query.trim().toLowerCase();
        if (!text) return rows;
        return rows.filter(row => row.serviceName.toLowerCase().includes(text));
    }, [rows, query]);

    const selected = rows.find(row => row.serviceId === selectedId) ?? null;

    // El nombre se corta con "…" y lo completo está en el tooltip del navegador
    const nameBody = (row: ServiceRow): ReactNode => (
        <span className="ss-name-cell__text" title={row.serviceName}>{row.serviceName}</span>
    );
    const attentionsBody = (row: ServiceRow) => formatInt(row.attentions);
    const levelTooltip = intl.formatMessage({ id: modeMessageId(mode, 'WaitLevelHint') }, { min: formatInt(ALERT_MIN_ATTENTIONS) });
    const waitBody = (row: ServiceRow) => {
        const change = row.waitChange === null ? null : formatSignedPercent(row.waitChange);
        const level = levels.get(row.serviceId) ?? null;
        return (
            <span className="ss-wait-cell">
                <span>{formatMinutes(row.avgWait)}</span>
                {change !== null && (
                    <span className="nv-help ss-delta">
                        <span aria-hidden="true">{change}</span>
                        <span className="nv-sr-only">
                            {intl.formatMessage({ id: 'ServicesStatisticsVsPreviousPercent' }, { value: change })}
                        </span>
                    </span>
                )}
                {level !== null && (
                    <HintTag id={`ss-level-${row.serviceId}`} className={LEVEL_TAG_CLASS[level]}
                        label={intl.formatMessage({ id: LEVEL_LABEL_ID[level] })} hint={levelTooltip} />
                )}
            </span>
        );
    };
    // Una sola celda para el turno y la duración real: "15 → 18,0 min" en consultorios, solo la duración en servicios
    const serviceBody = (row: ServiceRow) => (row.slotMinutes === null
        ? formatMinutes(row.avgService)
        : intl.formatMessage({ id: 'ServicesStatisticsSlotToReal' },
            { slot: formatInt(row.slotMinutes), real: formatMinutes(row.avgService) }));
    const occupancyBody = (row: ServiceRow) => (row.occupancyPercent === null
        ? EMPTY : formatPercent(row.occupancyPercent / 100));
    const reasonsBody = (row: ServiceRow) => <ReasonsCell intl={intl} row={row} />;

    return (
        <div className="nv-section">
            <div className="ss-toolbar">
                <div className="nv-field nv-w-m">
                    <label htmlFor="ss-services-search">{intl.formatMessage({ id: 'ServicesStatisticsSearch' })}</label>
                    <InputText id="ss-services-search" value={query} autoComplete="off"
                        onChange={e => setQuery(e.target.value)} />
                </div>
            </div>
            <p className="nv-sr-only">{intl.formatMessage({ id: modeMessageId(mode, 'TableHint') })}</p>
            <DataTable
                className="ss-table"
                tableStyle={{ tableLayout: 'fixed', width: '100%', minWidth: '1040px' }}
                value={visible}
                dataKey="serviceId"
                selectionMode="single"
                metaKeySelection={false}
                selection={selected}
                onSelectionChange={e => onToggle((e.value as ServiceRow | null)?.serviceId ?? ALL)}
                sortField={sort.field}
                sortOrder={sort.order}
                onSort={(e: DataTableSortEvent) => setSort({ field: e.sortField, order: (e.sortOrder ?? -1) as 1 | -1 })}
                emptyMessage={intl.formatMessage({ id: 'ServicesStatisticsNoResults' })}
                aria-label={intl.formatMessage({ id: modeMessageId(mode, 'TabServices') })}
            >
                <Column field="serviceName" header={intl.formatMessage({ id: modeMessageId(mode, 'ColService') })} sortable
                    body={nameBody} />
                <Column field="attentions" header={intl.formatMessage({ id: 'ServicesStatisticsColAttentions' })} sortable
                    body={attentionsBody} className="ss-center" headerClassName="ss-center" style={{ width: '96px' }} />
                <Column field="avgWait" header={intl.formatMessage({ id: 'ServicesStatisticsColWait' })} sortable
                    body={waitBody} className="ss-center" headerClassName="ss-center" style={{ width: '176px' }} />
                <Column field="avgService" sortable
                    header={<>{intl.formatMessage({ id: 'ServicesStatisticsColSlotDuration' })}
                        <InfoTip text={intl.formatMessage({ id: 'ServicesStatisticsColSlotDurationHint' })} /></>}
                    body={serviceBody} className="ss-center" headerClassName="ss-center" style={{ width: '176px' }} />
                <Column field="occupancyPercent" header={intl.formatMessage({ id: 'ServicesStatisticsColOccupancy' })} sortable
                    body={occupancyBody} className="ss-center" headerClassName="ss-center" style={{ width: '100px' }} />
                <Column header={intl.formatMessage({ id: 'ServicesStatisticsColWhy' })}
                    body={reasonsBody} style={{ width: '304px' }} />
            </DataTable>
        </div>
    );
}
